import type { User } from '@/types'

interface PermissionAccess {
  can_view?: boolean
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
  can_export?: boolean
  can_import?: boolean
}

interface RouteInfo {
  name: string
  icon: string
  parentId: number | null
  childId: number
}

interface AccessResult {
  can_view: boolean
  can_create: boolean
  can_edit: boolean
  can_delete: boolean
  can_export: boolean
  can_import: boolean
  route: RouteInfo | null
}

interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

interface UserRole {
  permissions?: Record<number | string, Record<number | string, PermissionAccess>> | string
}

export class AccessHelper {
  private user: User | null
  private userRoutes: UserRoute[]
  private permissions: Record<string, Record<string, PermissionAccess>> = {}

  constructor(user: User | null, userRoutes: UserRoute[] = []) {
    this.user = user
    this.userRoutes = userRoutes

    if (!user) {
      return
    }

    let userRoleObj: UserRole | null = null
    const userRole = (user as any)?.user_role

    if (userRole) {
      if (typeof userRole === 'string') {
        try {
          userRoleObj = JSON.parse(userRole)
        } catch (error) {
          console.error('[AccessHelper] Failed to parse user_role:', error)
          userRoleObj = null
        }
      } else {
        userRoleObj = userRole as UserRole
      }

      this.permissions = this.parsePermissions(userRoleObj?.permissions)
    }
  }

  private parsePermissions(
    permissionsStr: Record<number | string, Record<number | string, PermissionAccess>> | string | undefined
  ): Record<string, Record<string, PermissionAccess>> {
    try {
      if (!permissionsStr) {
        return {}
      }

      let parsed: any
      if (typeof permissionsStr === 'string') {
        parsed = JSON.parse(permissionsStr)
      } else {
        parsed = permissionsStr
      }

      const normalized: Record<string, Record<string, PermissionAccess>> = {}

      Object.keys(parsed).forEach((parentKey) => {
        const parentPermissions = parsed[parentKey]
        if (typeof parentPermissions === 'object' && parentPermissions !== null) {
          normalized[String(parentKey)] = {}
          Object.keys(parentPermissions).forEach((childKey) => {
            normalized[String(parentKey)][String(childKey)] = parentPermissions[childKey]
          })
        }
      })

      return normalized
    } catch (error) {
      console.error('[AccessHelper] Failed to parse permissions:', error)
      return {}
    }
  }

  private matchPath(routePath: string, currentPath: string): boolean {
    const routeSegments = routePath.split('/').filter(Boolean)
    const currentSegments = currentPath.split('/').filter(Boolean)

    if (routeSegments.length > currentSegments.length) {
      return false
    }

    const routePathNormalized = '/' + routeSegments.join('/')
    const currentPathNormalized = '/' + currentSegments.join('/')

    if (
      currentPathNormalized.startsWith(routePathNormalized + '/') ||
      currentPathNormalized === routePathNormalized
    ) {
      return true
    }

    if (routeSegments.length === currentSegments.length) {
      return routeSegments.every((segment, i) => {
        return segment.startsWith(':') || segment === currentSegments[i]
      })
    }

    return false
  }

  private findRouteWithParent(
    items: UserRoute[],
    targetPath: string,
    parentId: number | null = null
  ): { parentId: number | null; childId: number; name: string; icon: string } | null {
    const normalizedTarget = targetPath.replace(/\/$/, '') || '/'

    for (const item of items) {
      if (!item.path) continue

      const normalizedItemPath = item.path.replace(/\/$/, '') || '/'

      if (item.children && item.children.length > 0) {
        // First, try to find exact matches with IDs (main routes)
        // Then try matches without IDs (edit/create routes)
        const routesWithId: Array<{ route: any; match: boolean }> = []
        const routesWithoutId: Array<{ route: any; match: boolean }> = []

        for (const child of item.children) {
          if (!child.path) continue

          const childPathNormalized = child.path.replace(/\/$/, '') || '/'
          const combinedPath =
            normalizedItemPath === '/' ? childPathNormalized : normalizedItemPath + childPathNormalized

          // Check for exact match first
          const exactMatch = normalizedTarget === childPathNormalized || normalizedTarget === combinedPath
          // Check for path match
          const pathMatch = this.matchPath(child.path, normalizedTarget) || 
                           this.matchPath(combinedPath, normalizedTarget) ||
                           (normalizedTarget.startsWith(normalizedItemPath) && 
                            normalizedTarget.endsWith(childPathNormalized))

          if (exactMatch || pathMatch) {
            if (child.id) {
              routesWithId.push({ route: child, match: exactMatch })
            } else {
              routesWithoutId.push({ route: child, match: exactMatch })
            }
          }
        }

        // Prefer routes with IDs (main routes) over routes without IDs (edit/create routes)
        if (routesWithId.length > 0) {
          // Sort by exact match first, then by path match
          routesWithId.sort((a, b) => (b.match ? 1 : 0) - (a.match ? 1 : 0))
          const matched = routesWithId[0].route
          return {
            parentId: item.id || parentId,
            childId: matched.id || 0,
            name: matched.name,
            icon: matched.icon || '',
          }
        }

        // Fallback to routes without IDs if no route with ID matches
        if (routesWithoutId.length > 0) {
          routesWithoutId.sort((a, b) => (b.match ? 1 : 0) - (a.match ? 1 : 0))
          const matched = routesWithoutId[0].route
          return {
            parentId: item.id || parentId,
            childId: matched.id || 0,
            name: matched.name,
            icon: matched.icon || '',
          }
        }

        // Recursively search in children
        const found = this.findRouteWithParent(item.children, normalizedTarget, item.id || parentId)
        if (found) {
          return found
        }
      }

      if (this.matchPath(normalizedItemPath, normalizedTarget)) {
        const itemSegments = normalizedItemPath.split('/').filter(Boolean)
        const targetSegments = normalizedTarget.split('/').filter(Boolean)

        if (itemSegments.length === targetSegments.length) {
          return {
            parentId,
            childId: item.id || 0,
            name: item.name,
            icon: item.icon || '',
          }
        }
      }
    }
    return null
  }

  hasAccess(pathname?: string): AccessResult {
    const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/')
    const route = this.findRouteWithParent(this.userRoutes, currentPath)

    if (!route) {
      console.warn('[AccessHelper] Route not found for pathname:', currentPath, {
        userRoutes: this.userRoutes,
        availablePaths: this.userRoutes.flatMap((r: any) => [
          r.path,
          ...(r.children || []).map((c: any) => c.path).filter(Boolean)
        ])
      })
      return {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        can_import: false,
        route: null,
      }
    }

    const { parentId, childId, name, icon } = route
    const parentKey = parentId !== null ? String(parentId) : '0'
    const childKey = String(childId)
    const access = this.permissions[parentKey]?.[childKey]

    if (!access) {
      console.warn('[AccessHelper] Permissions not found for route:', {
        pathname: currentPath,
        parentId,
        childId,
        parentKey,
        childKey,
        availableParents: Object.keys(this.permissions),
        availableChildren: parentKey in this.permissions ? Object.keys(this.permissions[parentKey]) : []
      })
    }

    return {
      can_view: !!access?.can_view,
      can_create: !!access?.can_create,
      can_edit: !!access?.can_edit,
      can_delete: !!access?.can_delete,
      can_export: !!access?.can_export,
      can_import: !!access?.can_import,
      route: {
        name,
        icon,
        parentId,
        childId,
      },
    }
  }

  hasPermission(permission: keyof PermissionAccess, pathname?: string): boolean {
    const access = this.hasAccess(pathname)
    return access[permission] || false
  }
}

export default AccessHelper


